import { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { deleteChatUpload, listChatUploads, uploadChatUpload } from '../../services/chatService';
import useErrorModal from '../hooks/useErrorModal';
import useChatUploads from '../hooks/useChatUploads';
import {
  ACCEPTED_FILE_TYPES,
  formatFileSize,
  MAX_UPLOAD_COUNT,
  MAX_UPLOAD_SIZE,
  validateUploadFile,
} from './chatUploads';
import UploadIcon from '../assets/data-upload-upload-icon.svg';
import FileIcon from '../assets/file-icon.svg';
import CloseIcon from '../assets/close-icon.svg';
import '../reusable/UploadCard.css';

interface ChatUploadsPanelProps {
  eoaId: string;
  accessToken: string;
}

const ChatUploadsPanel = ({ eoaId, accessToken }: ChatUploadsPanelProps) => {
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const { uploads, uploading, uploadFiles, removeUpload } = useChatUploads({
    reloadKey: eoaId,
    maxCount: MAX_UPLOAD_COUNT,
    list: () => listChatUploads(eoaId, accessToken),
    upload: (file: File) => uploadChatUpload(eoaId, accessToken, file),
    remove: (uploadId: string) => deleteChatUpload(eoaId, accessToken, uploadId),
    loadErrorMessage: 'Failed to load your uploads',
    deleteErrorMessage: 'Failed to delete file',
  });

  const handleDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        const message =
          validateUploadFile(rejections[0].file) ?? rejections[0].errors[0]?.message ?? 'Unsupported file';
        handleOpenErrorModal(message, new Error(message));
      }
      if (accepted.length > 0) void uploadFiles(accepted);
    },
    [uploadFiles, handleOpenErrorModal]
  );

  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_UPLOAD_SIZE,
    multiple: true,
    onDrop: handleDrop,
  });

  return (
    <div className="chat-bottom-column chat-uploads-panel">
      <div className="chat-panel-heading">
        <h2 className="chat-panel-title">My uploads</h2>
        <p className="chat-panel-subtitle">
          Add PDFs, documents, or other files you&apos;d like the agent to refer to when crafting responses. Your upload
          will go to secured cloud storage that only XQ&apos;s internal models have access to.
        </p>
      </div>

      <div className="chat-uploads-content">
        <div {...getRootProps()} className={`drop-zone ${isDragAccept ? 'drag-active' : ''}`}>
          <input {...getInputProps()} />
          <img src={UploadIcon} alt="" className="upload-icon" />
          <p className="drop-zone-text">Drag &amp; drop to upload</p>
          <span className="drop-zone-text">or</span>
          <span className="drop-zone-text link-color"> browse files</span>
          <div className="drop-zone-helper-text-container">
            <p className="drop-zone-helper-text">Maximum file size is 25mb</p>
            <p className="drop-zone-helper-text">Supported file types are .docx, .pdf, and .txt</p>
          </div>
        </div>

        {(uploads.length > 0 || uploading.length > 0) && (
          <ul className="chat-uploads-list">
            {uploading.map((item) => (
              <li key={item.id} className="chat-uploads-item">
                <div className="chat-uploads-item-main">
                  <CircularProgress
                    className="chat-uploads-spinner"
                    size={16}
                    thickness={5}
                    sx={{ color: 'var(--text-gray)' }}
                  />
                  <div className="chat-file-chip-info">
                    <span className="chat-file-chip-name">{item.name}</span>
                    <span className="chat-file-chip-secondary">Processing data…</span>
                  </div>
                </div>
                <LinearProgress
                  className="chat-uploads-progress"
                  sx={{
                    backgroundColor: 'var(--divider-10)',
                    '& .MuiLinearProgress-bar': { backgroundColor: 'var(--accent-green-dark)' },
                  }}
                />
              </li>
            ))}
            {uploads.map((upload) => (
              <li key={upload.id} className="chat-uploads-item">
                <div className="chat-uploads-item-main">
                  <img src={FileIcon} alt="" className="chat-uploads-item-icon" />
                  <div className="chat-file-chip-info">
                    <span className="chat-file-chip-name">{upload.original_file_name}</span>
                    <span className="chat-file-chip-secondary">{formatFileSize(upload.size_bytes)}</span>
                  </div>
                  <button
                    type="button"
                    className="chat-uploads-item-remove"
                    aria-label={`Remove ${upload.original_file_name}`}
                    onClick={() => void removeUpload(upload.id)}
                  >
                    <img src={CloseIcon} alt="" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ChatUploadsPanel;
